import React, { useState, useEffect, useCallback } from 'react';
import { db } from './firebase.js';
import { collection, getDocs, addDoc, doc, setDoc } from 'firebase/firestore';
import {
  dismissWeatherAlert,
  filterActiveWeatherAlerts,
  loadDismissedWeatherAlertIds,
} from './weatherAlertUtils.js';

const zones = ['NCZ062', 'NCZ051', 'NCZ060', 'NCZ058'];

function WeatherAlertBanner({ user }) {
  const [alerts, setAlerts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => loadDismissedWeatherAlertIds());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [radarUrl, setRadarUrl] = useState('');
  const [showRadar, setShowRadar] = useState(false);

  const visibleAlerts = filterActiveWeatherAlerts(alerts, dismissedIds);

  const handleNewAlerts = useCallback(async (newAlerts) => {
    const alertIds = newAlerts.map((alert) => alert.properties.id);
    const storedAlertsRef = doc(db, 'weather_alerts', 'active');
    const storedDoc = await getDocs(collection(db, 'weather_alerts'));
    const storedIds = storedDoc.docs[0]?.data()?.ids || [];

    const novelAlerts = newAlerts.filter((alert) => !storedIds.includes(alert.properties.id));

    if (novelAlerts.length > 0 && user) {
      for (const alert of novelAlerts) {
        await addDoc(collection(db, 'message_board'), {
          title: `Weather Alert: ${alert.properties.headline}`,
          content: `${alert.properties.description}\nCAP Scope: ${alert.properties.scope || 'N/A'}`,
          createdAt: new Date(),
          userId: 'system',
          userEmail: 'system@nmr.com',
        });
      }
      await setDoc(storedAlertsRef, { ids: alertIds });
    }

    setAlerts(newAlerts);
  }, [user]);

  const fetchAlerts = useCallback(async () => {
    const url = `https://api.weather.gov/alerts/active?zone=${zones.join(',')}&status=actual&message_type=alert`;
    try {
      const response = await fetch(url, {
        headers: { 'User-Agent': 'NantahalaApp/1.0 (mikeechafin@gmail.com)' },
      });
      if (!response.ok) throw new Error('API failed');
      const data = await response.json();
      const relevantAlerts = (data.features || []).filter((feature) => {
        const event = feature.properties.event.toLowerCase();
        return (event.includes('warning') || event.includes('watch'))
          && feature.properties.status === 'Actual';
      });

      if (relevantAlerts.length > 0) {
        await handleNewAlerts(relevantAlerts);
      } else {
        setAlerts([]);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  }, [handleNewAlerts]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 600000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    if (!showRadar) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowRadar(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showRadar]);

  const handleDismiss = (alertId) => {
    dismissWeatherAlert(alertId);
    setDismissedIds(loadDismissedWeatherAlertIds());
  };

  const toggleExpanded = (alertId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId);
      else next.add(alertId);
      return next;
    });
  };

  const openRadar = (alert) => {
    let center = '35.3,-83.5';
    if (alert.properties.geometry) {
      const coords = alert.properties.geometry.coordinates[0][0];
      center = `${coords[1]},${coords[0]}`;
    }
    const url = `https://radar.weather.gov/?settings=v1_eyJhZ2VuZGEiOnsiaWQiOm51bGwsImNlbnRlciI6Wy${btoa(center)},OSwiem9vbSI6OX0sImJhc2UiOiJzdGFuZGFyZCIsImNvdW50eSI6ZmFsc2UsImN3YSI6ZmFsc2UsInN0YXRlIjpmYWxzZSwibWVudSI6dHJ1ZSwic2hvcnRGdXNlZE9ubHkiOnRydWV9`;
    setRadarUrl(url);
    setShowRadar(true);
  };

  if (visibleAlerts.length === 0) return null;

  return (
    <>
      <div className="weather-alert-stack" role="region" aria-label="Weather alerts">
        {visibleAlerts.map((alert) => {
          const alertId = alert.properties.id;
          const isExpanded = expandedIds.has(alertId);
          return (
            <article key={alertId} className="weather-alert-banner">
              <div className="weather-alert-banner-inner">
                <div className="weather-alert-copy">
                  <p className="weather-alert-headline">{alert.properties.headline}</p>
                  <p className={`weather-alert-description ${isExpanded ? 'is-expanded' : ''}`}>
                    {alert.properties.description}
                  </p>
                </div>
                <div className="weather-alert-actions">
                  <button
                    type="button"
                    className="weather-alert-btn weather-alert-btn-secondary"
                    onClick={() => toggleExpanded(alertId)}
                  >
                    {isExpanded ? 'Less' : 'Details'}
                  </button>
                  <button
                    type="button"
                    className="weather-alert-btn weather-alert-btn-secondary"
                    onClick={() => openRadar(alert)}
                  >
                    Radar
                  </button>
                  <button
                    type="button"
                    className="weather-alert-btn weather-alert-btn-dismiss"
                    onClick={() => handleDismiss(alertId)}
                    aria-label={`Dismiss weather alert: ${alert.properties.headline}`}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {showRadar ? (
        <div
          className="weather-alert-radar-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Weather radar"
          onClick={() => setShowRadar(false)}
        >
          <div
            className="weather-alert-radar-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="weather-alert-radar-close"
              onClick={() => setShowRadar(false)}
              aria-label="Close radar"
            >
              Close
            </button>
            <iframe src={radarUrl} title="Doppler radar" className="weather-alert-radar-frame" />
          </div>
        </div>
      ) : null}
    </>
  );
}

export default WeatherAlertBanner;